/**
 * <s-garment-spin> — 360° garment viewer built from a numbered sequence of real photos,
 * one per angle. It never fakes angles: it only shows the frames it is given.
 *
 * data-frames   JSON array of frame URLs, in rotation order (frame 1 faces forward).
 *               Frames load only once the viewer is visible, so the rack stays fast.
 * data-autoplay "true" starts a slow turn once loaded (skipped for reduced motion).
 * data-seconds  seconds per full turn while playing.
 * data-label-play / data-label-pause / data-label-angle ("%ANGLE%" placeholder) for the controls.
 *
 * Children:
 *   img[data-spin-image]   still frame shown while loading; becomes the live frame.
 *   [data-spin-stage]      role="slider" surface: drag (mouse), swipe (touch), Left/Right/Home keys.
 *   [data-spin-status]     loading / hint text.
 *   button[data-spin-toggle] with command="--toggle" pauses/plays.
 *   buttons with command="--frame" and data-frame jump to an angle (e.g. Front/Back).
 */
class SGarmentSpin extends HTMLElement {
  #ac;
  #io;
  #frames = [];
  #index = 0;
  #state = 'idle';
  #playing = false;
  #userPaused = false;
  #raf = 0;
  #last = 0;
  #acc = 0;
  #drag = null;
  #reduced = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.img = this.querySelector('[data-spin-image]');
    this.stage = this.querySelector('[data-spin-stage]');
    this.status = this.querySelector('[data-spin-status]');
    this.toggle = this.querySelector('[data-spin-toggle]');
    try {
      this.#frames = JSON.parse(this.dataset.frames || '[]');
    } catch {
      this.#frames = [];
    }
    if (!this.img || !this.stage || this.#frames.length < 2) return;

    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#reduced = motion.matches;
    motion.addEventListener('change', (event) => {
      this.#reduced = event.matches;
      if (this.#reduced) this.#stop();
    }, opts);

    this.addEventListener('command', this.#onCommand, opts);
    this.stage.addEventListener('pointerdown', this.#onDown, opts);
    this.stage.addEventListener('pointermove', this.#onMove, opts);
    this.stage.addEventListener('pointerup', this.#onUp, opts);
    this.stage.addEventListener('pointercancel', this.#onUp, opts);
    this.stage.addEventListener('keydown', this.#onKey, opts);
    this.stage.addEventListener('dragstart', (event) => event.preventDefault(), opts);

    // The viewer sits inside a hidden detail view; it starts loading when that view opens.
    this.#io = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible) {
        this.#load();
        if (this.#state === 'ready') this.#maybeAutoplay();
      } else {
        this.#stop();
      }
    });
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
    cancelAnimationFrame(this.#raf);
  }

  get #seconds() {
    const value = Number(this.dataset.seconds);
    return value > 0 ? value : 14;
  }

  #load() {
    if (this.#state !== 'idle') return;
    this.#state = 'loading';
    this.setAttribute('aria-busy', 'true');
    let done = 0;
    const total = this.#frames.length;
    // Ask Shopify's CDN for a width that suits this screen, so large HD frames stay light on phones.
    const needed = this.stage.clientWidth * Math.min(window.devicePixelRatio || 1, 2);
    const width = [600, 900, 1200, 1600, 2000].find((size) => size >= needed) || 2000;
    this.#frames = this.#frames.map((url) => `${url}${url.includes('?') ? '&' : '?'}width=${width}`);
    const loads = this.#frames.map((url) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
      return image.decode().catch(() => {}).finally(() => {
        done += 1;
        this.style.setProperty('--spin-progress', String(done / total));
      });
    });
    Promise.all(loads).then(() => {
      this.#state = 'ready';
      this.removeAttribute('aria-busy');
      this.dataset.ready = '';
      // Frames replace the responsive still, so drop its srcset before swapping.
      this.img.removeAttribute('srcset');
      this.img.removeAttribute('sizes');
      this.#show(this.#index);
      if (this.status) this.status.textContent = this.status.dataset.ready || '';
      if (this.toggle) this.toggle.disabled = false;
      this.#maybeAutoplay();
    });
  }

  #maybeAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.#reduced || this.#userPaused) return;
    this.#play();
  }

  #show(index) {
    const count = this.#frames.length;
    this.#index = ((index % count) + count) % count;
    if (this.#state === 'ready') this.img.src = this.#frames[this.#index];
    const angle = Math.round((this.#index / count) * 360);
    this.stage.setAttribute('aria-valuenow', String(angle));
    const label = this.dataset.labelAngle || '%ANGLE%°';
    this.stage.setAttribute('aria-valuetext', label.replace('%ANGLE%', String(angle)));
  }

  #play() {
    if (this.#playing || this.#state !== 'ready') return;
    this.#playing = true;
    this.#last = performance.now();
    this.#acc = 0;
    this.#syncToggle();
    const step = this.#seconds * 1000 / this.#frames.length;
    const tick = (now) => {
      if (!this.#playing) return;
      this.#acc += now - this.#last;
      this.#last = now;
      if (this.#acc >= step) {
        const frames = Math.floor(this.#acc / step);
        this.#acc -= frames * step;
        this.#show(this.#index + frames);
      }
      this.#raf = requestAnimationFrame(tick);
    };
    this.#raf = requestAnimationFrame(tick);
  }

  #stop() {
    this.#playing = false;
    cancelAnimationFrame(this.#raf);
    this.#syncToggle();
  }

  #pauseByUser() {
    this.#userPaused = true;
    this.#stop();
  }

  #syncToggle() {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', String(!this.#playing));
    const label = this.#playing ? this.dataset.labelPause : this.dataset.labelPlay;
    if (label) {
      this.toggle.setAttribute('aria-label', label);
      this.toggle.title = label;
    }
    this.toggle.dataset.playing = String(this.#playing);
  }

  #onCommand = (event) => {
    switch (event.command) {
      case '--toggle':
        if (this.#playing) {
          this.#pauseByUser();
        } else {
          this.#userPaused = false;
          this.#play();
        }
        break;
      case '--frame': {
        this.#pauseByUser();
        this.#show(Number(event.source?.dataset.frame || 0));
        break;
      }
    }
  };

  #onDown = (event) => {
    if (this.#state !== 'ready' || event.button > 0) return;
    this.#pauseByUser();
    this.#drag = { x: event.clientX, index: this.#index, id: event.pointerId };
    this.stage.setPointerCapture(event.pointerId);
    this.stage.dataset.dragging = '';
  };

  #onMove = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.id) return;
    // Dragging across the full width of the viewer turns the garment once.
    const perFrame = Math.max(this.stage.clientWidth, 1) / this.#frames.length;
    const dx = event.clientX - this.#drag.x;
    const direction = document.documentElement.dir === 'rtl' ? 1 : -1;
    this.#show(this.#drag.index + direction * Math.round(dx / perFrame));
  };

  #onUp = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.id) return;
    this.#drag = null;
    delete this.stage.dataset.dragging;
  };

  #onKey = (event) => {
    if (this.#state !== 'ready') return;
    const keys = { ArrowLeft: -1, ArrowRight: 1, Home: 0 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    event.stopPropagation();
    this.#pauseByUser();
    if (event.key === 'Home') this.#show(0);
    else this.#show(this.#index + keys[event.key]);
  };
}

if (!customElements.get('s-garment-spin')) {
  customElements.define('s-garment-spin', SGarmentSpin);
}
